import React, { useEffect, useState, useRef } from 'react';
import { Button, Space, Table, TimePicker, Input, Modal, Typography, Spin, message, DatePicker, Alert, Tag, Tabs, Select, Row, Col, Card, Divider, Tour, ConfigProvider } from 'antd';
import type { TourProps } from 'antd';
import { DeleteOutlined, PlusOutlined, EditOutlined, ClockCircleOutlined, CalendarOutlined, InfoCircleOutlined, SaveOutlined, CheckCircleFilled, QuestionCircleOutlined } from '@ant-design/icons';
import { OpeningHours, ClosingHours, ClosingTime } from '../types/businessHours';
import { businessHoursService } from '../services/businessHoursService';
import dayjs from 'dayjs';
import type { Dayjs } from 'dayjs';
import AdminLayout from '../components/AdminLayout';
import '../styles/AdminPages.css';
import viVN from 'antd/locale/vi_VN';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;
const { Option } = Select;

// Helpers to generate time options
const generateHourOptions = () => {
  const options = [];
  for (let i = 0; i < 24; i++) {
    const value = i.toString().padStart(2, '0');
    options.push(<Option key={value} value={value}>{value}</Option>);
  }
  return options;
};

const generateMinuteOptions = () => {
  const options = [];
  for (let i = 0; i < 60; i++) {
    const value = i.toString().padStart(2, '0');
    options.push(<Option key={value} value={value}>{value}</Option>);
  }
  return options;
};

// Custom style for select components
const selectStyle = {
  width: 80,
  height: 42,
  fontSize: '16px',
  textAlign: 'center' as 'center'
};

// Custom style for time labels
const timeLabel = {
  fontSize: '16px',
  fontWeight: 'normal' as 'normal',
  marginRight: '8px',
  color: 'rgba(0, 0, 0, 0.85)'
};

// Custom style for the save button
const saveButtonStyle = {
  height: 42,
  fontSize: '15px',
  padding: '0 24px',
  borderRadius: '4px'
};

const BusinessHoursManagement: React.FC = () => {
  const [openingHours, setOpeningHours] = useState<OpeningHours | null>(null);
  const [closingHours, setClosingHours] = useState<ClosingHours | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [newClosingTime, setNewClosingTime] = useState<Partial<ClosingTime>>({});
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [editingClosingTime, setEditingClosingTime] = useState<ClosingTime | null>(null);
  const [activeTab, setActiveTab] = useState('weekdays');
  
  // State for weekday time selects
  const [weekdayOpenHour, setWeekdayOpenHour] = useState<string>('07');
  const [weekdayOpenMinute, setWeekdayOpenMinute] = useState<string>('00');
  const [weekdayCloseHour, setWeekdayCloseHour] = useState<string>('14');
  const [weekdayCloseMinute, setWeekdayCloseMinute] = useState<string>('30');
  
  // State for weekend time selects
  const [weekendOpenHour, setWeekendOpenHour] = useState<string>('07');
  const [weekendOpenMinute, setWeekendOpenMinute] = useState<string>('00');
  const [weekendCloseHour, setWeekendCloseHour] = useState<string>('18');
  const [weekendCloseMinute, setWeekendCloseMinute] = useState<string>('30');

  // Tour state and refs
  const [isTourOpen, setIsTourOpen] = useState(false);
  const titleRef = useRef(null);
  const openingHoursRef = useRef(null);
  const tabsRef = useRef(null);
  const addButtonRef = useRef(null);
  const tableContainerRef = useRef(null);

  const tourSteps: TourProps['steps'] = [
    {
      title: 'Quản lý giờ mở cửa',
      description: 'Chào mừng bạn đến với trang Quản lý giờ mở cửa! Đây là nơi bạn có thể thiết lập giờ hoạt động hàng ngày của cửa hàng và quản lý các ngày đóng cửa đặc biệt.',
      target: () => titleRef.current,
    },
    {
      title: 'Giờ mở cửa hàng ngày',
      description: 'Khu vực này cho phép bạn thiết lập giờ mở cửa thường ngày của cửa hàng. Bạn có thể cấu hình riêng biệt cho các ngày trong tuần và cuối tuần.',
      target: () => openingHoursRef.current,
    },
    {
      title: 'Chuyển đổi giữa các loại ngày',
      description: 'Sử dụng các tab này để chuyển đổi giữa cài đặt giờ cho các ngày trong tuần (Thứ 2-6) và cuối tuần (Thứ 7-CN). Mỗi loại ngày có thể có giờ mở cửa khác nhau.',
      target: () => tabsRef.current,
    },
    {
      title: 'Thêm giờ đóng cửa đặc biệt',
      description: 'Nhấn vào đây để thêm những ngày đóng cửa đặc biệt như ngày lễ, bảo trì, hoặc các sự kiện đặc biệt. Điều này giúp khách hàng biết được khi nào cửa hàng không hoạt động.',
      target: () => addButtonRef.current,
    },
    {
      title: 'Danh sách lịch đóng cửa',
      description: 'Bảng này hiển thị tất cả các lịch đóng cửa đặc biệt đã được thiết lập. Bạn có thể xem, chỉnh sửa hoặc xóa các lịch này khi cần.',
      target: () => tableContainerRef.current,
    },
    {
      title: 'Kết thúc hướng dẫn!',
      description: 'Cảm ơn bạn đã xem hướng dẫn này. Bây giờ bạn có thể quản lý giờ hoạt động của cửa hàng một cách hiệu quả và thông báo chính xác cho khách hàng.',
      target: () => titleRef.current,
    },
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (openingHours) {
      // Update select values from openingHours
      if (openingHours.weekdays) {
        const [openHour, openMinute] = openingHours.weekdays.openTime.split(':');
        const [closeHour, closeMinute] = openingHours.weekdays.closeTime.split(':');
        setWeekdayOpenHour(openHour);
        setWeekdayOpenMinute(openMinute);
        setWeekdayCloseHour(closeHour);
        setWeekdayCloseMinute(closeMinute);
      }
      
      if (openingHours.weekends) {
        const [openHour, openMinute] = openingHours.weekends.openTime.split(':');
        const [closeHour, closeMinute] = openingHours.weekends.closeTime.split(':');
        setWeekendOpenHour(openHour);
        setWeekendOpenMinute(openMinute);
        setWeekendCloseHour(closeHour);
        setWeekendCloseMinute(closeMinute);
      }
    }
  }, [openingHours]);

  const loadData = async () => {
    try {
      setLoading(true);
      const openingData = await businessHoursService.getOpeningHours();
      const closingData = await businessHoursService.getClosingHours();
      setOpeningHours(openingData);
      setClosingHours(closingData);
    } catch (error) {
      message.error('Không thể tải dữ liệu: ' + error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveWeekdayHours = async () => {
    if (!openingHours) return;

    const newOpenTime = `${weekdayOpenHour}:${weekdayOpenMinute}`;
    const newCloseTime = `${weekdayCloseHour}:${weekdayCloseMinute}`;

    // Validate time
    if (newOpenTime === newCloseTime) {
      message.error('Giờ mở cửa và đóng cửa không thể giống nhau');
      return;
    }

    try {
      setUpdating(true);
      const updatedHours: OpeningHours = {
        ...openingHours,
        weekdays: {
          openTime: newOpenTime,
          closeTime: newCloseTime
        }
      };

      await businessHoursService.updateOpeningHours(updatedHours);
      setOpeningHours(updatedHours);
      message.success('Cập nhật giờ mở cửa các ngày trong tuần thành công');
    } catch (error) {
      message.error('Không thể cập nhật giờ mở cửa các ngày trong tuần: ' + error);
    } finally {
      setUpdating(false);
    }
  };

  const handleSaveWeekendHours = async () => {
    if (!openingHours) return;

    const newOpenTime = `${weekendOpenHour}:${weekendOpenMinute}`;
    const newCloseTime = `${weekendCloseHour}:${weekendCloseMinute}`;

    // Validate time
    if (newOpenTime === newCloseTime) {
      message.error('Giờ mở cửa và đóng cửa không thể giống nhau');
      return;
    }

    try {
      setUpdating(true);
      const updatedHours: OpeningHours = {
        ...openingHours,
        weekends: {
          openTime: newOpenTime,
          closeTime: newCloseTime
        }
      };

      await businessHoursService.updateOpeningHours(updatedHours);
      setOpeningHours(updatedHours);
      message.success('Cập nhật giờ mở cửa cuối tuần thành công');
    } catch (error) {
      message.error('Không thể cập nhật giờ mở cửa cuối tuần: ' + error);
    } finally {
      setUpdating(false);
    }
  };

  const handleWeekdayHoursChange = async (values: [Dayjs | null, Dayjs | null] | null) => {
    // Keeping this function for reference, but it's no longer used
  };

  const handleWeekendHoursChange = async (values: [Dayjs | null, Dayjs | null] | null) => {
    // Keeping this function for reference, but it's no longer used
  };

  const handleAddClosingTime = async () => {
    if (!newClosingTime.startTime || !newClosingTime.endTime || !newClosingTime.description) return;

    try {
      setUpdating(true);
      const newClosingHours: ClosingHours = {
        id: closingHours?.id || 'default',
        closingTimes: [
          ...(closingHours?.closingTimes || []),
          {
            id: Date.now().toString(),
            startTime: newClosingTime.startTime,
            endTime: newClosingTime.endTime,
            description: newClosingTime.description,
          },
        ],
      };

      await businessHoursService.updateClosingHours(newClosingHours);
      setClosingHours(newClosingHours);
      setIsModalOpen(false);
      setNewClosingTime({});
      message.success('Thêm giờ đóng cửa thành công');
    } catch (error) {
      message.error('Không thể thêm giờ đóng cửa: ' + error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteClosingTime = async (id: string) => {
    if (!closingHours) return;

    Modal.confirm({
      title: 'Xác nhận xóa',
      content: 'Bạn có chắc muốn xóa giờ đóng cửa này?',
      okText: 'Đồng ý',
      cancelText: 'Hủy',
      onOk: async () => {
        try {
          setUpdating(true);
          const newClosingHours: ClosingHours = {
            ...closingHours,
            closingTimes: closingHours.closingTimes.filter((time) => time.id !== id),
          };

          await businessHoursService.updateClosingHours(newClosingHours);
          setClosingHours(newClosingHours);
          message.success('Xóa giờ đóng cửa thành công');
        } catch (error) {
          message.error('Không thể xóa giờ đóng cửa: ' + error);
        } finally {
          setUpdating(false);
        }
      }
    });
  };

  const handleEditClosingTime = (record: ClosingTime) => {
    setEditingClosingTime(record);
    setNewClosingTime({
      id: record.id,
      startTime: record.startTime,
      endTime: record.endTime,
      description: record.description,
    });
    setIsModalOpen(true);
  };

  const handleSaveClosingTime = async () => {
    if (!newClosingTime.startTime || !newClosingTime.endTime || !newClosingTime.description || !closingHours) return;

    try {
      setUpdating(true);
      let updatedClosingTimes: ClosingTime[];

      if (editingClosingTime) {
        // Editing existing time
        updatedClosingTimes = closingHours.closingTimes.map(time =>
          time.id === editingClosingTime.id
            ? { ...newClosingTime as ClosingTime }
            : time
        );
      } else {
        // Adding new time
        updatedClosingTimes = [
          ...closingHours.closingTimes,
          {
            id: Date.now().toString(),
            startTime: newClosingTime.startTime,
            endTime: newClosingTime.endTime,
            description: newClosingTime.description,
          },
        ];
      }

      const newClosingHours: ClosingHours = {
        id: closingHours.id || 'default',
        closingTimes: updatedClosingTimes,
      };

      await businessHoursService.updateClosingHours(newClosingHours);
      setClosingHours(newClosingHours);
      setIsModalOpen(false);
      setNewClosingTime({});
      setEditingClosingTime(null);
      message.success(editingClosingTime ? 'Cập nhật giờ đóng cửa thành công' : 'Thêm giờ đóng cửa thành công');
    } catch (error) {
      message.error(editingClosingTime ? 'Không thể cập nhật giờ đóng cửa' : 'Không thể thêm giờ đóng cửa');
    } finally {
      setUpdating(false);
    }
  };

  const handleModalCancel = () => {
    setIsModalOpen(false);
    setNewClosingTime({});
    setEditingClosingTime(null);
  };

  const columns = [
    {
      title: 'Thời gian',
      dataIndex: 'time',
      key: 'time',
      render: (_: any, record: ClosingTime) => {
        const start = dayjs(record.startTime).format('DD/MM/YYYY HH:mm');
        const end = dayjs(record.endTime).format('DD/MM/YYYY HH:mm');
        return `${start} - ${end}`;
      },
      sorter: (a: ClosingTime, b: ClosingTime) =>
        dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf()
    },
    {
      title: 'Mô tả',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: 'Thao tác',
      key: 'action',
      render: (_: any, record: ClosingTime) => (
        <Space>
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEditClosingTime(record)}
            disabled={updating}
          />
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => handleDeleteClosingTime(record.id)}
            disabled={updating}
          />
        </Space>
      ),
    },
  ];

  const disabledDate = (current: Dayjs) => {
    // Disable dates before today
    return current && current < dayjs().startOf('day');
  };

  const handleRangePickerChange = (values: [Dayjs | null, Dayjs | null] | null) => {
    if (values && values[0] && values[1]) {
      // Kiểm tra xem thời gian bắt đầu có phải trong quá khứ không
      if (values[0].isBefore(dayjs())) {
        message.error('Không thể chọn thời gian trong quá khứ');
        return;
      }

      // Kiểm tra xem thời gian kết thúc có sau thời gian bắt đầu không
      if (values[1].isBefore(values[0])) {
        message.error('Thời gian kết thúc phải sau thời gian bắt đầu');
        return;
      }

      setNewClosingTime({
        ...newClosingTime,
        startTime: values[0].format('YYYY-MM-DD HH:mm'),
        endTime: values[1].format('YYYY-MM-DD HH:mm'),
      });
    }
  };

  // Time selection component for weekdays
  const renderWeekdayTimeSelection = () => (
    <Card className="time-selection-card" style={{ border: '1px solid #f0f0f0', borderRadius: '8px' }}>
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <Text style={{ fontSize: '16px', color: '#595959' }}>
          Cài đặt thời gian mở cửa và đóng cửa cho các ngày trong tuần (Thứ 2 - Thứ 6)
        </Text>
      </div>
      
      <div style={{ maxWidth: '700px', margin: '0 auto' }}>
        <Row gutter={[16, 24]} align="middle" style={{ marginBottom: '20px' }}>
          <Col span={6} style={{ textAlign: 'right' }}>
            <Text style={timeLabel}>Giờ mở cửa:</Text>
          </Col>
          <Col span={18}>
            <Space align="center" size="middle">
              <Select 
                value={weekdayOpenHour}
                onChange={(value) => setWeekdayOpenHour(value)}
                style={selectStyle}
                disabled={updating}
                dropdownStyle={{ textAlign: 'center' }}
              >
                {generateHourOptions()}
              </Select>
              <Text style={{ fontSize: '18px', margin: '0 4px' }}>:</Text>
              <Select 
                value={weekdayOpenMinute}
                onChange={(value) => setWeekdayOpenMinute(value)}
                style={selectStyle}
                disabled={updating}
                dropdownStyle={{ textAlign: 'center' }}
              >
                {generateMinuteOptions()}
              </Select>
            </Space>
          </Col>
        </Row>
        
        <Row gutter={[16, 24]} align="middle" style={{ marginBottom: '20px' }}>
          <Col span={6} style={{ textAlign: 'right' }}>
            <Text style={timeLabel}>Giờ đóng cửa:</Text>
          </Col>
          <Col span={18}>
            <Space align="center" size="middle">
              <Select 
                value={weekdayCloseHour}
                onChange={(value) => setWeekdayCloseHour(value)}
                style={selectStyle}
                disabled={updating}
                dropdownStyle={{ textAlign: 'center' }}
              >
                {generateHourOptions()}
              </Select>
              <Text style={{ fontSize: '18px', margin: '0 4px' }}>:</Text>
              <Select 
                value={weekdayCloseMinute}
                onChange={(value) => setWeekdayCloseMinute(value)}
                style={selectStyle}
                disabled={updating}
                dropdownStyle={{ textAlign: 'center' }}
              >
                {generateMinuteOptions()}
              </Select>
            </Space>
          </Col>
        </Row>
        
        <Row style={{ marginTop: '30px', marginBottom: '20px', justifyContent: 'center' }}>
          <Button 
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveWeekdayHours}
            disabled={updating}
            loading={updating}
            style={saveButtonStyle}
            size="large"
          >
            Xác nhận thay đổi
          </Button>
        </Row>
        
        {openingHours?.weekdays && (
          <Alert
            message={
              <Space>
                <CheckCircleFilled style={{ color: '#52c41a' }} />
                <Text style={{ fontSize: '14px' }}>
                  Các ngày trong tuần (Thứ 2 - Thứ 6) cửa hàng mở cửa từ <strong>{openingHours.weekdays.openTime}</strong> đến <strong>{openingHours.weekdays.closeTime}</strong>
                </Text>
              </Space>
            }
            type="success"
            showIcon={false}
            style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px' }}
          />
        )}
      </div>
    </Card>
  );

  // Time selection component for weekends
  const renderWeekendTimeSelection = () => (
    <Card className="time-selection-card" style={{ border: '1px solid #f0f0f0', borderRadius: '8px' }}>
      <div style={{ textAlign: 'center', marginBottom: '20px' }}>
        <Text style={{ fontSize: '16px', color: '#595959' }}>
          Cài đặt thời gian mở cửa và đóng cửa cho cuối tuần (Thứ 7 - Chủ nhật)
        </Text>
      </div>
      
      <div style={{ maxWidth: '700px', margin: '0 auto' }}>
        <Row gutter={[16, 24]} align="middle" style={{ marginBottom: '20px' }}>
          <Col span={6} style={{ textAlign: 'right' }}>
            <Text style={timeLabel}>Giờ mở cửa:</Text>
          </Col>
          <Col span={18}>
            <Space align="center" size="middle">
              <Select 
                value={weekendOpenHour}
                onChange={(value) => setWeekendOpenHour(value)}
                style={selectStyle}
                disabled={updating}
                dropdownStyle={{ textAlign: 'center' }}
              >
                {generateHourOptions()}
              </Select>
              <Text style={{ fontSize: '18px', margin: '0 4px' }}>:</Text>
              <Select 
                value={weekendOpenMinute}
                onChange={(value) => setWeekendOpenMinute(value)}
                style={selectStyle}
                disabled={updating}
                dropdownStyle={{ textAlign: 'center' }}
              >
                {generateMinuteOptions()}
              </Select>
            </Space>
          </Col>
        </Row>
        
        <Row gutter={[16, 24]} align="middle" style={{ marginBottom: '20px' }}>
          <Col span={6} style={{ textAlign: 'right' }}>
            <Text style={timeLabel}>Giờ đóng cửa:</Text>
          </Col>
          <Col span={18}>
            <Space align="center" size="middle">
              <Select 
                value={weekendCloseHour}
                onChange={(value) => setWeekendCloseHour(value)}
                style={selectStyle}
                disabled={updating}
                dropdownStyle={{ textAlign: 'center' }}
              >
                {generateHourOptions()}
              </Select>
              <Text style={{ fontSize: '18px', margin: '0 4px' }}>:</Text>
              <Select 
                value={weekendCloseMinute}
                onChange={(value) => setWeekendCloseMinute(value)}
                style={selectStyle}
                disabled={updating}
                dropdownStyle={{ textAlign: 'center' }}
              >
                {generateMinuteOptions()}
              </Select>
            </Space>
          </Col>
        </Row>
        
        <Row style={{ marginTop: '30px', marginBottom: '20px', justifyContent: 'center' }}>
          <Button 
            type="primary"
            icon={<SaveOutlined />}
            onClick={handleSaveWeekendHours}
            disabled={updating}
            loading={updating}
            style={saveButtonStyle}
            size="large"
          >
            Xác nhận thay đổi
          </Button>
        </Row>
        
        {openingHours?.weekends && (
          <Alert
            message={
              <Space>
                <CheckCircleFilled style={{ color: '#52c41a' }} />
                <Text style={{ fontSize: '14px' }}>
                  Cuối tuần (Thứ 7 - Chủ nhật) cửa hàng mở cửa từ <strong>{openingHours.weekends.openTime}</strong> đến <strong>{openingHours.weekends.closeTime}</strong>
                </Text>
              </Space>
            }
            type="success"
            showIcon={false}
            style={{ backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: '4px' }}
          />
        )}
      </div>
    </Card>
  );

  return (
    <AdminLayout>
      <div className="admin-page-header">
        <div className="admin-page-title">
          <h1 ref={titleRef}>
            <ClockCircleOutlined style={{ color: '#1890ff', marginRight: '8px' }} />
            Quản lý giờ mở cửa
          </h1>
          <Tag color="blue">{closingHours?.closingTimes?.length || 0} lịch đóng cửa</Tag>
          <Button 
            type="primary" 
            icon={<QuestionCircleOutlined />} 
            onClick={() => setIsTourOpen(true)}
            style={{ marginLeft: 8 }}
          >
            Hướng dẫn
          </Button>
        </div>
      </div>

      <Spin spinning={loading || updating}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          {/* Opening Hours Card */}
          <div className="admin-form-section" style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)', borderRadius: '8px', padding: '24px' }} ref={openingHoursRef}>
            <h3 className="admin-form-section-title" style={{ marginBottom: '20px', fontSize: '18px', display: 'flex', alignItems: 'center' }}>
              <ClockCircleOutlined style={{ color: '#1890ff', marginRight: '10px', fontSize: '22px' }} />
              <span>Giờ Mở Cửa Hàng Ngày</span>
            </h3>
            
            <div ref={tabsRef}>
              <Tabs 
                activeKey={activeTab} 
                onChange={setActiveTab} 
                type="card"
                size="large"
                style={{ marginBottom: '20px' }}
                tabBarStyle={{ marginBottom: '24px', borderBottom: '1px solid #f0f0f0' }}
              >
                <TabPane tab="Thứ 2 - Thứ 6" key="weekdays">
                  {renderWeekdayTimeSelection()}
                </TabPane>
                
                <TabPane tab="Thứ 7 - Chủ Nhật" key="weekends">
                  {renderWeekendTimeSelection()}
                </TabPane>
              </Tabs>
            </div>
          </div>

          {/* Special Closing Hours Card */}
          <div className="admin-form-section" style={{ boxShadow: '0 1px 2px rgba(0,0,0,0.05)', borderRadius: '8px', padding: '24px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 className="admin-form-section-title" style={{ margin: 0, fontSize: '18px', display: 'flex', alignItems: 'center' }}>
                <CalendarOutlined style={{ color: '#ff4d4f', marginRight: '10px', fontSize: '22px' }} />
                <span>Giờ Đóng Cửa Đặc Biệt</span>
              </h3>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={() => {
                  setEditingClosingTime(null);
                  setNewClosingTime({});
                  setIsModalOpen(true);
                }}
                disabled={updating}
                className="admin-button"
                size="large"
                ref={addButtonRef}
              >
                Thêm giờ đóng cửa
              </Button>
            </div>

            <div className="admin-table-container" ref={tableContainerRef}>
              <Table
                className="admin-table"
                columns={[
                  {
                    title: 'Thời gian',
                    dataIndex: 'time',
                    key: 'time',
                    render: (_: any, record: ClosingTime) => {
                      const start = dayjs(record.startTime).format('DD/MM/YYYY HH:mm');
                      const end = dayjs(record.endTime).format('DD/MM/YYYY HH:mm');
                      return (
                        <Space>
                          <CalendarOutlined style={{ color: '#1890ff' }} />
                          <span>{start} - {end}</span>
                        </Space>
                      );
                    },
                    sorter: (a: ClosingTime, b: ClosingTime) =>
                      dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf()
                  },
                  {
                    title: 'Mô tả',
                    dataIndex: 'description',
                    key: 'description',
                    render: (text: string) => (
                      <Space>
                        <InfoCircleOutlined style={{ color: '#8c8c8c' }} />
                        <span>{text}</span>
                      </Space>
                    )
                  },
                  {
                    title: 'Thao tác',
                    key: 'action',
                    render: (_: any, record: ClosingTime) => (
                      <Space>
                        <Button
                          type="text"
                          icon={<EditOutlined />}
                          onClick={() => handleEditClosingTime(record)}
                          disabled={updating}
                        />
                        <Button
                          type="text"
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => handleDeleteClosingTime(record.id)}
                          disabled={updating}
                        />
                      </Space>
                    ),
                  },
                ]}
                dataSource={closingHours?.closingTimes.sort((a, b) =>
                  dayjs(a.startTime).valueOf() - dayjs(b.startTime).valueOf()
                ) || []}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 5 }}
              />
            </div>
          </div>
        </Space>

        {/* Add/Edit Modal */}
        <Modal
          title={
            <Space>
              <CalendarOutlined style={{ color: editingClosingTime ? '#1890ff' : '#52c41a' }} />
              <span>{editingClosingTime ? "Chỉnh sửa giờ đóng cửa" : "Thêm giờ đóng cửa mới"}</span>
            </Space>
          }
          open={isModalOpen}
          onOk={handleSaveClosingTime}
          onCancel={handleModalCancel}
          okText={editingClosingTime ? "Lưu thay đổi" : "Thêm mới"}
          cancelText="Hủy"
          confirmLoading={updating}
          okButtonProps={{
            disabled: !newClosingTime.startTime || !newClosingTime.endTime || !newClosingTime.description,
            className: "admin-button"
          }}
          cancelButtonProps={{ className: "admin-button" }}
          className="admin-modal"
        >
          <div className="admin-form-container">
            <div className="admin-form-section">
              <h3 className="admin-form-section-title">Thời gian</h3>
              <RangePicker
                showTime={{ format: 'HH:mm' }}
                format="DD/MM/YYYY HH:mm"
                placeholder={['Thời gian bắt đầu', 'Thời gian kết thúc']}
                style={{ width: '100%' }}
                onChange={handleRangePickerChange}
                disabledDate={disabledDate}
                disabled={updating}
                value={newClosingTime.startTime && newClosingTime.endTime ? [
                  dayjs(newClosingTime.startTime),
                  dayjs(newClosingTime.endTime)
                ] : null}
              />
            </div>

            <div className="admin-form-section">
              <h3 className="admin-form-section-title">Mô tả</h3>
              <Input.TextArea
                placeholder="Mô tả lý do đóng cửa"
                value={newClosingTime.description || ''}
                onChange={(e) => setNewClosingTime({
                  ...newClosingTime,
                  description: e.target.value,
                })}
                rows={4}
                disabled={updating}
              />
            </div>
          </div>
        </Modal>

        {/* Tour component */}
        <ConfigProvider locale={viVN}>
          <Tour
            open={isTourOpen}
            onClose={() => setIsTourOpen(false)}
            steps={tourSteps}
            indicatorsRender={(current, total) => (
              <span>{current + 1} / {total}</span>
            )}
            type="primary"
            arrow={true}
            placement="bottom"
            mask={{
              style: {
                boxShadow: 'inset 0 0 15px #333',
              },
            }}
          />
        </ConfigProvider>
      </Spin>
    </AdminLayout>
  );
};

export default BusinessHoursManagement; 